import { action, computed, observable, makeObservable } from 'mobx';

type State = 'pending' | 'resolved' | 'rejected';

interface BasePromiseState<T> {
    readonly promise?: Promise<T>;
    readonly state: State;
    readonly value: T | null;
    readonly error: any | null;

    readonly isPending: boolean;
    readonly isCompleted: boolean;
    readonly isResolved: boolean;
    readonly isRejected: boolean;
}

export interface PromisePendingState<T> extends BasePromiseState<T> {
    readonly state: 'pending';
    readonly value: null;
    readonly error: null;
    readonly isPending: true;
    readonly isCompleted: false;
    readonly isResolved: false;
    readonly isRejected: false;
}

export interface PromiseResolvedState<T> extends BasePromiseState<T> {
    readonly state: 'resolved';
    readonly value: T;
    readonly error: null;
    readonly isPending: false;
    readonly isCompleted: true;
    readonly isResolved: true;
    readonly isRejected: false;
}

export interface PromiseRejectedState<T> extends BasePromiseState<T> {
    readonly state: 'rejected';
    readonly value: null;
    readonly error: any;
    readonly isPending: false;
    readonly isCompleted: true;
    readonly isResolved: false;
    readonly isRejected: true;
}

export type PromiseFulfilledState<T> = PromiseResolvedState<T> | PromiseRejectedState<T>;

export type PromiseState<T> =
    PromisePendingState<T>
    | PromiseResolvedState<T>
    | PromiseRejectedState<T>;

class PromiseStateClass<T> implements BasePromiseState<T> {
    @observable state: State;

    @observable.ref value: T | null;
    @observable.ref error: any | null;

    constructor(readonly promise: Promise<T>) {
        makeObservable(this);
        this.value = null;
        this.error = null;
        this.state = 'pending';
        promise.then(this.onResolved, this.onRejected);
    }

    @action
    private onResolved = (result: T) => {
        this.value = result;
        this.state = 'resolved';
    };

    @action
    private onRejected = (error: any) => {
        this.error = error;
        this.state = 'rejected';
    };

    @computed
    get isCompleted(): boolean {
        return this.state === 'rejected' || this.state === 'resolved';
    };

    @computed
    get isPending(): boolean {
        return this.state === 'pending';
    }

    @computed
    get isResolved(): boolean {
        return this.state === 'resolved';
    }

    @computed
    get isRejected(): boolean {
        return this.state === 'rejected';
    }
}

export function watchPromise<T>(promise: Promise<T>): PromiseState<T> {
    return new PromiseStateClass(promise) as PromiseState<T>;
}
